import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import './RbpLetter.css';
import { requestContentFragment } from '../../utils/contentFragmentUtil';
import SessionStorage from '../../utils/SessionStorage';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import { AcquisitionFlow, checkIfInValidAcquisitionFlow } from '../../utils/acquisition-utils';
import { Letter } from '../../types/responses/submit-application';

interface IRbpLetterProps extends MappedComponentProperties {}

export const RbpLetterEditConfig = {
    emptyLabel: 'RBP Letter',
    isEmpty: (props: PropsWithChildren<IRbpLetterProps>) => {
        return !props || props.isInEditor;
    },
};


const RbpLetter = (): JSX.Element => {
    const [rbpLetter, setRbpLetter] = useState('');

    const getFormattedDate = useCallback((date: Date) => {
        return new Date(date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    }, []);

    const getDataAsList = function (list: any) {
        if (!list || !list.length) {
            return '';
        }
        return (
            `<ul className={'RbpLetter__list'}>
                ${list.map((el: string, i: number) => {
                return (
                    `<li key=${i}>
                            ${el}
                         </li>`
                );
            }).join('')}
            </ul>`
        );
    };

    const buildLetter = async (letter: string, appData: Record<string, any>) => {
        const creditBureauData = appData.CreditBureauData || {};
        const personalInfo = await SessionStorage.tryGetCipher('personalInfo', {});

        const allLetterData = {
            ...appData,
            ...creditBureauData,
            ...personalInfo,
        };

        for (const [key, value] of Object.entries(allLetterData)) {
            const regx = new RegExp('{' + key + '}', 'g');
            if (Array.isArray(value)) {
                letter = letter.replace(regx, getDataAsList(value));
                continue;
            } 

            if (key.toLowerCase().includes('date')) {
                letter = letter.replace(regx, getFormattedDate(value as Date));
            }

            letter = letter.replace(regx, value as string);
        }

        letter = letter.replace(/{CurrentDate}/g, getFormattedDate(new Date()))
            .replace(/{CustomerAddressLine1}/g, allLetterData.MailingAddressLine1)
            .replace(/{CustomerAddressLine2}/g, allLetterData.MailingAddressLine2)
            .replace(/{City}/g, allLetterData.MailingCity)
            .replace(/{State}/g, allLetterData.MailingState)
            .replace(/{ZipCode}/g, allLetterData.MailingZipCode)
            .replace(/{InquiryCode}/g, allLetterData.ReservationNumber);

        setRbpLetter(letter);
    };

    const getRbpLetter = (appData: Record<string, any>) => {
        requestContentFragment({
            path: '/content/dam/cob-corp-acquisition/content-fragments/declineletter',
            filters: {
                declineLetterNumber: appData.LetterName,
            },
        }).then(({ data }) => {
            if (data?.[0]?.declineLetterNumber === appData?.LetterName && data?.[0]?.declineLetterContent) {
                buildLetter(data[0].declineLetterContent, appData);
            }
        }).catch((error) => {
            // eslint-disable-next-line no-console
            console.debug(error);
        });
    };

    useEffect(() => {
        const getAppData = async () => {
            const isDataInNewFormat = SessionStorage.verify('flowApplicationData');
            const approvedData = isDataInNewFormat ? SessionStorage.getObject('flowApplicationData') : await SessionStorage.tryGetCipher('app-approved-data', null);
            const isPreQualOrPqOfOneOrPha = checkIfInValidAcquisitionFlow([AcquisitionFlow.PQ, AcquisitionFlow.PqOfOne, AcquisitionFlow.PHA]);
            const riskBasedBypass = !!SessionStorage.get('bypassRbpEsign');

            if (!isPreQualOrPqOfOneOrPha || !approvedData || riskBasedBypass) {
                return;
            }

            if (isDataInNewFormat) {
                const mappedRbpData = {};

                const Name = `${approvedData?.parties?.[0]?.firstName} ${approvedData?.parties?.[0]?.middleInitial ?? ''} ${approvedData?.parties?.[0]?.lastName}`;
                const address = approvedData?.parties?.[0]?.addresses?.find((addr) => addr?.usageType === 'MAILING');
                const letterData: Letter = approvedData?.letter;

                Object.assign(mappedRbpData, { 
                    Name: Name,
                    ReservationNumber: approvedData?.application?.reservationNumber,
                    MailingAddressLine1: address?.addressLine1,
                    MailingAddressLine2: address?.addressLine2,
                    MailingCity: address?.city,
                    MailingState: address?.state,
                    MailingZipCode: address?.zipCode,
                    LetterName: letterData.templateCode,
                });

                letterData?.content?.variables?.forEach((variable) => {
                    Object.assign(mappedRbpData, { [variable.key as string]: variable?.value });
                });

                letterData?.content?.listVariables?.forEach((variable) => {
                    Object.assign(mappedRbpData, { [variable.key as string]: variable?.values });
                });

                getRbpLetter(mappedRbpData);
            } else if (approvedData.HasRBPNotification) {
                const personalInfo = await SessionStorage.tryGetCipher('personalInfo', {});
                const creditBureauData = approvedData.CreditBureauData || {};
                getRbpLetter({ ...approvedData, ...creditBureauData, ...personalInfo });
            }
        };
        getAppData();
    }, []);

    return (<div id='reb-letter' className='RbpLetter'>{transformHTMLParserLinks(rbpLetter)}</div>);
};

export default RbpLetter;